import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type Board, type Clip, type Tag } from '@air/api/types';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { selectedItemsSelector } from '@air/redux-selected-items';

import { DeleteCanvasConfirmationModal } from '~/components/Canvas/DeleteCanvasConfirmationModal';
import { useGetCanvasMenuOptionsCallback } from '~/components/Canvas/hooks/useGetCanvasMenuOptionsCallback';
import { MoveCanvasToBoardModal } from '~/components/Canvas/MoveCanvasToBoardModal/MoveCanvasToBoardModal';
import { filterCanvasTopBarMoreMenuOptions } from '~/components/Canvas/utils/filterCanvasTopBarMoreMenuOptions';
import { BatchExportImagesModal } from '~/components/ExportImagesModal/BatchExportImagesModal';
import { GalleryItemType } from '~/components/Gallery/types';
import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { canSharePrivateAsset } from '~/components/SharePrivateClipModal/hooks/useSharePrivateAsset';
import { applySelectionBarOverflowTuck } from '~/components/v2/components/private-selection-bar/hooks/applySelectionBarOverflowTuck';
import { usePrivateCanShareCollection } from '~/components/v2/components/private-selection-bar/hooks/usePrivateCanShareCollection';
import {
  DEFAULT_SELECTION_BAR_LAYOUT_TIER_FLAGS,
  type SelectionBarLayoutTierFlags,
} from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarLayoutTier';
import { type SelectionBarActions } from '~/components/v2/components/shared/selectionBar/selectionBarActions';
import { useWorkspaceAgentSelectionMenuHeroOptions } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentSelectionMenuHeroOptions';
import { useGetPrivateAssetMenuOptionsCallback } from '~/components/v2/hooks/useGetPrivateAssetMenuOptionsCallback';
import { useGetPrivateBoardMenuOptionsCallback } from '~/components/v2/hooks/useGetPrivateBoardMenuOptionsCallback';
import { usePrivateSelectionMenuOptions } from '~/components/v2/hooks/usePrivateSelectionMenuOptions';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useCanCreateCanvasInCurrentLocation } from '~/hooks/canvases/useCanCreateCanvasInCurrentLocation';
import { useOpenClipInCanvas } from '~/hooks/canvases/useOpenClipInCanvas';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import {
  hasOnlyCanvasesSelectedSelector,
  hasOnlyClipsSelectedSelector,
  hasSelectionAcrossMultipleViewsSelector,
  selectedBoardsSelector,
  selectedClipsHasUnfavorited,
  selectedFileClipsSelector,
  selectedMediaClipsSelector,
} from '~/store/selectedItems/selectors';
import { isSelectableGalleryItem, type SelectableGalleryCanvasItem } from '~/store/selectedItems/types';
import { useListCanvases } from '~/swr-hooks/canvases/useListCanvases';
import { canCompareSelection } from '~/utils/canCompareSelection';
import { getBatchCropAndEditOnCanvasHandler } from '~/utils/canvases/getCropAndEditOnCanvasHandler';
import { isClipExportable } from '~/utils/Imgix';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canDownloadVersion,
  canEditClipTags,
  canRemoveClipTags,
} from '~/utils/permissions/assetPermissions';
import { canCreateShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export type UsePrivateSelectionBarMenuProps = {
  collectionsEnabled: boolean;
  canDownloadAllClips: boolean;
  canDownloadAllBoards: boolean;
  onBulkDownload: () => void;
  onBoardFavorite: (board: Board) => void;
  onBoardShare: (boardId: string) => void;
  onCanvasShare: (canvasId: string) => void;
  onBoardDownload: (boardId: string) => void;
  onClipFavorite: (clips: Clip[]) => void;
  onClipShare: (clip: Clip) => void;
  onAssetDownload: (id: string) => void;
  onSingleClipEditTags: (clip: Clip, tags: Tag[]) => void;
  onMultipleClipsEditTags: () => void;
  onCollectionShare: () => void;
  onCompare: () => void;
  onAddToCanvas?: () => void;
  onInsert?: () => void;
  insertCount?: number;
  layoutTierFlags?: SelectionBarLayoutTierFlags;
};

export type UsePrivateSelectionBarMenuResult = {
  menuOptions: DropdownMenuOption[];
  actions: SelectionBarActions;
};

export const usePrivateSelectionBarMenu = ({
  collectionsEnabled,
  canDownloadAllClips,
  canDownloadAllBoards,
  onBulkDownload,
  onBoardFavorite,
  onBoardShare,
  onCanvasShare,
  onBoardDownload,
  onClipFavorite,
  onClipShare,
  onAssetDownload,
  onSingleClipEditTags,
  onMultipleClipsEditTags,
  onCollectionShare,
  onCompare,
  onAddToCanvas,
  onInsert,
  insertCount,
  layoutTierFlags = DEFAULT_SELECTION_BAR_LAYOUT_TIER_FLAGS,
}: UsePrivateSelectionBarMenuProps): UsePrivateSelectionBarMenuResult => {
  const selectedItems = useSelector(selectedItemsSelector);
  const selectedBoardItems = useSelector(selectedBoardsSelector);
  const selectedMediaClipItems = useSelector(selectedMediaClipsSelector);
  const selectedFileItems = useSelector(selectedFileClipsSelector);
  const hasOnlyClipsSelected = useSelector(hasOnlyClipsSelectedSelector);
  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const hasSelectionAcrossMultipleViews = useSelector(hasSelectionAcrossMultipleViewsSelector);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const store = useAirStore();
  const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
  const { generativeCanvasEnabled, ui2026 } = useTemporaryFeatureFlags();

  const { getBoardMenuOptionsCallback } = useGetPrivateBoardMenuOptionsCallback();
  const { getAssetMenuOptionsCallback } = useGetPrivateAssetMenuOptionsCallback();
  const selectedItem0 = selectedItems[0];
  const singleSelectedCanvas =
    selectedItems.length === 1 &&
    selectedItem0 &&
    isSelectableGalleryItem(selectedItem0) &&
    selectedItem0.type === GalleryItemType.canvas
      ? selectedItem0.item
      : undefined;
  const { getCanvasMenuOptionsCallback } = useGetCanvasMenuOptionsCallback(
    ui2026 ? currentBoardId : singleSelectedCanvas?.boardId,
  );
  const [showBatchExportImagesModal] = useAirModal(BatchExportImagesModal);
  const [showMoveCanvasToBoardModal] = useAirModal(MoveCanvasToBoardModal);
  const [showDeleteCanvasConfirmationModal] = useAirModal(DeleteCanvasConfirmationModal);

  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const { applyAgentSelectionBarEllipsisMenuTransforms, customFieldsSubmenuClassName, enrichCustomFieldsMenuOptions } =
    useWorkspaceAgentSelectionMenuHeroOptions();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { agentSessions: isAgentSessionsEnabled } = usePrivateAddOnFeatures();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const { canShareCollection, itemCount: collectionItemCount } = usePrivateCanShareCollection();
  const { currentWorkspace } = useCurrentWorkspace();
  const { canCreateCanvas } = useCanCreateCanvasInCurrentLocation();
  const { openClipInCanvas } = useOpenClipInCanvas();
  const { total: canvasCount } = useListCanvases({
    workspaceId: currentWorkspace?.id,
  });
  const { canConsumeMoreCredits } = useCanConsumeMoreCredits();

  return useMemo(() => {
    let options = [...getSelectionMenuOptions('action-bar', true)];
    let editTags: SelectionBarActions['editTags'];
    let favorite: SelectionBarActions['favorite'];
    let editTagsOption: DropdownMenuOption | null = null;
    let favoriteOption: DropdownMenuOption | null = null;
    let downloadAction: (() => void) | null = canDownloadAllClips && canDownloadAllBoards ? onBulkDownload : null;
    let download: SelectionBarActions['download'] = downloadAction
      ? { hasMultiple: true, onSelect: downloadAction }
      : undefined;
    let share: SelectionBarActions['share'];
    let addToBoard: SelectionBarActions['addToBoard'];
    let customFields: SelectionBarActions['customFields'];
    let insert: SelectionBarActions['insert'];
    let addToCanvas: SelectionBarActions['addToCanvas'];
    const compare: SelectionBarActions['compare'] = canCompareSelection({
      hasSelectionAcrossMultipleViews,
      isSelectAllActive: selectAll,
      selectedItemCount: selectedItems.length,
      selectedMediaClips: selectedMediaClipItems,
    })
      ? { onSelect: onCompare }
      : undefined;
    let shareAction: (() => void) | null = null;

    const selectedCount = selectedItems.length;
    const selectedItem = selectedCount === 1 ? selectedItems[0] : null;
    const isSingleCanvasSelected = selectedItem?.type === GalleryItemType.canvas;
    const isSingleCanvasOnly = selectedCount === 1 && isSingleCanvasSelected;
    const hasCanvasSelected = selectedItems.some((item) => item.type === GalleryItemType.canvas);
    const isSplitView = generativeCanvasEnabled && isRightPanelOpen;
    const isInsertHidden = !isSplitView || isSingleCanvasOnly || !canConsumeMoreCredits;
    const showAddToCanvas =
      !isSplitView && generativeCanvasEnabled && selectedCount > 0 && !isSingleCanvasOnly && canConsumeMoreCredits;
    const isSharePrimary = !showAddToCanvas;

    // In non-split view, keep "add to board" as a dedicated button for multi-select.
    if (!isSplitView) {
      options = options.filter((option) => option.id !== 'add-to-board' && option.id !== 'add-to-board-separator');
    }

    if (selectedItems.length === 1) {
      const selectedItem = selectedItems[0];

      if (selectedItem.type === GalleryItemType.board) {
        const board = selectedBoardItems[0];
        const boardPermissions = getBoardPermissions(board.id);
        const canDownloadBoard = canGenerateZip(boardPermissions);

        options = getBoardMenuOptionsCallback({ item: board })({ trackLocation: 'action-bar' });
        // Boards don't support tag editing
        editTags = undefined;
        favorite = { bookmarked: board.hasCurrentUser, onSelect: () => onBoardFavorite(board) };
        favoriteOption = {
          ...getDropdownMenuOption(board.hasCurrentUser ? 'unfavorite' : 'favorite'),
          onSelect: () => onBoardFavorite(board),
          type: 'item',
        };
        shareAction = () => onBoardShare(board.id);
        share = canViewShortUrl(boardPermissions)
          ? { kind: 'item', isPrimary: isSharePrimary, onSelect: shareAction }
          : undefined;
        downloadAction = () => onBoardDownload(board.id);
        download = canDownloadBoard ? { hasMultiple: false, onSelect: downloadAction } : undefined;
      } else if (selectedItem.type === GalleryItemType.asset) {
        const clip = selectedMediaClipItems[0];
        const clipPermissions = getClipPermissions(clip.id);
        const canEditTags = canEditClipTags(clipPermissions) && canRemoveClipTags(clipPermissions);
        const canShare = canSharePrivateAsset(clipPermissions);
        const canDownloadClip = canDownloadVersion(clipPermissions);
        options = getAssetMenuOptionsCallback({ item: clip })({ trackLocation: 'action-bar' });
        editTags = canEditTags ? { onSelect: () => onSingleClipEditTags(clip, clip.tags || []) } : undefined;
        editTagsOption = canEditTags
          ? {
              ...getDropdownMenuOption('edit-tags'),
              onSelect: () => onSingleClipEditTags(clip, clip.tags || []),
              type: 'item',
            }
          : null;
        favorite = { bookmarked: clip.bookmarked, onSelect: () => onClipFavorite([clip]) };
        favoriteOption = {
          ...getDropdownMenuOption(clip.bookmarked ? 'unfavorite' : 'favorite'),
          onSelect: () => onClipFavorite([clip]),
          type: 'item',
        };
        shareAction = () => onClipShare(clip);
        share = canShare ? { kind: 'item', isPrimary: isSharePrimary, onSelect: shareAction } : undefined;
        downloadAction = () => onAssetDownload(clip.id);
        download = canDownloadClip ? { hasMultiple: false, onSelect: downloadAction } : undefined;
      } else if (selectedItem.type === GalleryItemType.file) {
        const file = selectedFileItems[0];
        const clipPermissions = getClipPermissions(file.id);
        const canEditTags = canEditClipTags(clipPermissions) && canRemoveClipTags(clipPermissions);
        const canShare = canSharePrivateAsset(clipPermissions);
        const canDownloadClip = canDownloadVersion(clipPermissions);
        options = getAssetMenuOptionsCallback({ item: file })({ trackLocation: 'action-bar' });
        editTags = canEditTags ? { onSelect: () => onSingleClipEditTags(file, file.tags || []) } : undefined;
        editTagsOption = canEditTags
          ? {
              ...getDropdownMenuOption('edit-tags'),
              onSelect: () => onSingleClipEditTags(file, file.tags || []),
              type: 'item',
            }
          : null;
        favorite = { bookmarked: file.bookmarked, onSelect: () => onClipFavorite([file]) };
        favoriteOption = {
          ...getDropdownMenuOption(file.bookmarked ? 'unfavorite' : 'favorite'),
          onSelect: () => onClipFavorite([file]),
          type: 'item',
        };
        shareAction = () => onClipShare(file);
        share = canShare ? { kind: 'item', isPrimary: isSharePrimary, onSelect: shareAction } : undefined;
        downloadAction = () => onAssetDownload(file.id);
        download = canDownloadClip ? { hasMultiple: false, onSelect: downloadAction } : undefined;
      } else if (selectedItem.type === GalleryItemType.canvas) {
        if (singleSelectedCanvas) {
          options = filterCanvasTopBarMoreMenuOptions(
            getCanvasMenuOptionsCallback({
              item: singleSelectedCanvas,
              trackLocation: 'action-bar',
              boardId: singleSelectedCanvas.boardId ?? undefined,
            }),
          );
        }
        const boardId = currentBoardId;

        if (boardId && singleSelectedCanvas) {
          const boardPermissions = getBoardPermissions(boardId);
          shareAction = () => onCanvasShare(singleSelectedCanvas.id);
          share = canViewShortUrl(boardPermissions)
            ? { kind: 'item', isPrimary: isSharePrimary, onSelect: shareAction }
            : undefined;
        }
        editTags = generativeCanvasEnabled ? undefined : editTags;
        download = undefined;
        downloadAction = null;
      }
    } else if (hasOnlyClipsSelected) {
      const areAllClipsFavorited = !selectedClipsHasUnfavorited(store.getState());
      // Combine both media clips and files for favoriting
      const allSelectedClips = [...selectedMediaClipItems, ...selectedFileItems];
      const canEditAllCustomFields = allSelectedClips.every(({ id }) =>
        canChangeAssetCustomFields(getClipPermissions(id)),
      );
      const canAddAllAssetsToABoard = allSelectedClips.every(({ id }) => canAddAssetToBoard(getClipPermissions(id)));
      favorite = { bookmarked: areAllClipsFavorited, onSelect: () => onClipFavorite(allSelectedClips) };
      editTags = { onSelect: onMultipleClipsEditTags };
      editTagsOption = {
        ...getDropdownMenuOption('edit-tags'),
        onSelect: onMultipleClipsEditTags,
        type: 'item',
      };
      favoriteOption = {
        ...getDropdownMenuOption(areAllClipsFavorited ? 'unfavorite' : 'favorite'),
        onSelect: () => onClipFavorite(allSelectedClips),
        type: 'item',
      };
      customFields = canEditAllCustomFields
        ? {
            options: enrichCustomFieldsMenuOptions(getCustomFieldsMenuOptions({ assets: allSelectedClips })),
          }
        : undefined;
      addToBoard = canAddAllAssetsToABoard
        ? {
            onSelect: () =>
              showAddAssetsToBoardsModal({
                clipIds: allSelectedClips.map((clip) => clip.id),
                trackLocation: 'action-bar',
                parentBoardId: currentBoardId,
              }),
          }
        : undefined;
      const hasExportableClip = allSelectedClips.some((clip) => isClipExportable(clip));

      if (hasExportableClip) {
        options.push({
          ...getDropdownMenuOption('export-as'),
          label: 'Export selection as',
          onSelect: () =>
            showBatchExportImagesModal({
              clips: allSelectedClips,
              onCropAndEditOnCanvas: getBatchCropAndEditOnCanvasHandler({
                canCreateCanvas,
                clips: allSelectedClips,
                openClipInCanvas,
                openedFrom: 'action_bar_export_as',
              }),
              workspaceId: currentWorkspace?.id,
            }),
          type: 'item',
        });
      }
    }
    // Bulk operations (tags, sharing, collections) are not supported for canvases; only move and delete apply.
    // Intersect per-canvas options so actions only appear when all selected canvases support them.
    else if (hasOnlyCanvasesSelected && selectedCount > 1) {
      download = undefined;
      downloadAction = null;
      share = undefined;
      shareAction = null;

      const selectedCanvases = selectedItems
        .filter((item): item is SelectableGalleryCanvasItem => item.type === GalleryItemType.canvas)
        .map((item) => item.item);
      const selectedCanvasIds = selectedCanvases.map((canvas) => canvas.id);
      const targetCanvasOptionIds = new Set(['move-to', 'delete']);
      const allCanvasOptions = selectedCanvases.map((canvas) =>
        getCanvasMenuOptionsCallback({
          item: canvas,
          trackLocation: 'action-bar',
          boardId: canvas.boardId,
        }),
      );
      const canvasOptionIdsPerCanvas = allCanvasOptions.map((opts) =>
        opts.filter((option) => targetCanvasOptionIds.has(option.id)).map((option) => option.id),
      );
      const commonOptionIds = canvasOptionIdsPerCanvas.reduce<Set<string>>((acc, optionIds, index) => {
        if (index === 0) {
          return new Set(optionIds);
        }

        return new Set(optionIds.filter((id) => acc.has(id)));
      }, new Set<string>());

      const firstCanvasOptions = allCanvasOptions[0] ?? [];

      const commonBoardId =
        selectedCanvases.length > 0 &&
        selectedCanvases.every((canvas) => canvas.boardId === selectedCanvases[0].boardId)
          ? selectedCanvases[0].boardId
          : undefined;
      const moveTemplateOption = firstCanvasOptions.find(
        (option): option is Extract<DropdownMenuOption, { type: 'item' }> =>
          option.id === 'move-to' && option.type === 'item',
      );
      const deleteTemplateOption = firstCanvasOptions.find(
        (option): option is Extract<DropdownMenuOption, { type: 'item' }> =>
          option.id === 'delete' && option.type === 'item',
      );
      const bulkCanvasOptions: DropdownMenuOption[] = [];

      if (commonOptionIds.has('move-to') && moveTemplateOption) {
        bulkCanvasOptions.push({
          ...moveTemplateOption,
          onSelect: () =>
            showMoveCanvasToBoardModal({
              canvasIds: selectedCanvasIds,
              currentBoardId: commonBoardId,
              trackLocation: 'action-bar',
            }),
        });
      }

      if (commonOptionIds.has('delete') && deleteTemplateOption) {
        bulkCanvasOptions.push({
          ...deleteTemplateOption,
          onSelect: () =>
            showDeleteCanvasConfirmationModal({
              canvasIds: selectedCanvasIds,
              boardId: currentBoardId,
            }),
        });
      }

      options = bulkCanvasOptions;
    }

    if (selectedItems.length > 1 && collectionsEnabled && !hasOnlyCanvasesSelected) {
      const allSelectedClips = [...selectedMediaClipItems, ...selectedFileItems];
      const canShareMultiSelect =
        allSelectedClips.some((clip) => canCreateShortUrl(getClipPermissions(clip.id))) ||
        selectedBoardItems.some((board) => canCreateShortUrl(getBoardPermissions(board.id)));

      if (canShareMultiSelect && canShareCollection) {
        shareAction = onCollectionShare;
        share = {
          kind: 'collection',
          isPrimary: isSharePrimary,
          onSelect: shareAction,
          collectionItemCount,
        };
      }
    }

    if (!isInsertHidden && generativeCanvasEnabled && canvasCount > 0 && !hasOnlyCanvasesSelected && onInsert) {
      insert = { count: insertCount, onSelect: onInsert };
    }

    if (
      !isSplitView &&
      generativeCanvasEnabled &&
      selectedCount > 0 &&
      !hasCanvasSelected &&
      onAddToCanvas &&
      canConsumeMoreCredits
    ) {
      addToCanvas = { count: selectedCount > 1 ? selectedCount : undefined, onSelect: onAddToCanvas };
    }

    const editInCanvasOption = options.find(
      (option): option is Extract<DropdownMenuOption, { type: 'sub' }> =>
        option.id === 'edit-in-canvas' && option.type === 'sub',
    );
    const editInCanvas: SelectionBarActions['editInCanvas'] = editInCanvasOption
      ? { options: editInCanvasOption.options }
      : undefined;

    const tucked = applySelectionBarOverflowTuck({
      options,
      actions: {
        compare,
        editTags,
        favorite,
        addToBoard,
        customFields,
        editInCanvas,
        insert,
        download,
        share,
        addToCanvas,
      },
      editTagsOption,
      favoriteOption,
      customFieldsSubmenu: customFields
        ? {
            ...getDropdownMenuOption('bulk-edit-custom-fields'),
            className: customFieldsSubmenuClassName,
            options: customFields.options,
            type: 'sub',
          }
        : null,
      downloadAction,
      shareAction,
      isSplitView,
      hidePrefixActions: layoutTierFlags.hidePrefixActions,
      agentSessionsEnabled: isAgentSessionsEnabled === true,
      selectedCount,
    });

    const menuOptions =
      isAgentSessionsEnabled === true
        ? applyAgentSelectionBarEllipsisMenuTransforms(tucked.options, {
            editTagsOption,
            hasOnlyClipsSelected,
            selectedCount: selectedItems.length,
          })
        : tucked.options;

    return {
      menuOptions,
      actions: tucked.actions,
    };
  }, [
    applyAgentSelectionBarEllipsisMenuTransforms,
    getSelectionMenuOptions,
    canDownloadAllClips,
    canDownloadAllBoards,
    onBulkDownload,
    onInsert,
    generativeCanvasEnabled,
    isAgentSessionsEnabled,
    isRightPanelOpen,
    onAddToCanvas,
    selectedItems,
    getCustomFieldsMenuOptions,
    customFieldsSubmenuClassName,
    enrichCustomFieldsMenuOptions,
    selectedBoardItems,
    getBoardPermissions,
    getBoardMenuOptionsCallback,
    onBoardFavorite,
    onBoardShare,
    showAddAssetsToBoardsModal,
    showBatchExportImagesModal,
    canCreateCanvas,
    openClipInCanvas,
    currentWorkspace?.id,
    currentBoardId,
    onBoardDownload,
    selectedMediaClipItems,
    getClipPermissions,
    getAssetMenuOptionsCallback,
    getCanvasMenuOptionsCallback,
    showDeleteCanvasConfirmationModal,
    showMoveCanvasToBoardModal,
    onCanvasShare,
    onSingleClipEditTags,
    onClipFavorite,
    onClipShare,
    onAssetDownload,
    selectedFileItems,
    singleSelectedCanvas,
    hasOnlyClipsSelected,
    hasOnlyCanvasesSelected,
    store,
    onMultipleClipsEditTags,
    onCollectionShare,
    collectionsEnabled,
    insertCount,
    canvasCount,
    canShareCollection,
    collectionItemCount,
    canConsumeMoreCredits,
    layoutTierFlags,
    hasSelectionAcrossMultipleViews,
    onCompare,
    selectAll,
  ]);
};
